/**
 * TB Mukt Bharat - Achievements Page
 * Fast & Lightweight - No animations
 */

import React, { useState, useMemo, useCallback } from 'react'
import { Trophy, Star, Target, Users, Heart, BookOpen, Award } from 'lucide-react'
import {
  GovLayout,
  GovPageTitle,
  GovCard,
  GovButton,
  GovEmptyState,
} from '@/components/layout/GovLayout'

// ================================================================================
// TYPES
// ================================================================================
interface Achievement {
  id: string
  title: string
  description: string
  icon: string
  points: number
  unlockedAt?: string
  progress?: number
  total?: number
  category: 'awareness' | 'social' | 'health' | 'community'
}

// ================================================================================
// CONSTANTS
// ================================================================================
const CATEGORY_FILTERS = [
  { id: 'all', label: 'All', icon: Star },
  { id: 'awareness', label: 'Awareness', icon: BookOpen },
  { id: 'social', label: 'Social', icon: Users },
  { id: 'health', label: 'Health', icon: Heart },
  { id: 'community', label: 'Community', icon: Target },
] as const

const MOCK_ACHIEVEMENTS: Achievement[] = [
  {
    id: '1',
    title: 'First Post',
    description: 'Share your first post about TB awareness',
    icon: '📝',
    points: 50,
    unlockedAt: '2026-06-01T10:00:00',
    category: 'awareness',
  },
  {
    id: '2',
    title: 'Knowledge Seeker',
    description: 'Complete 10 knowledge quizzes',
    icon: '📚',
    points: 100,
    progress: 7,
    total: 10,
    category: 'awareness',
  },
  {
    id: '3',
    title: 'Community Builder',
    description: 'Create your first group',
    icon: '👥',
    points: 150,
    unlockedAt: '2026-06-02T14:30:00',
    category: 'community',
  },
  {
    id: '4',
    title: 'Health Champion',
    description: 'Log your health activities for 7 days',
    icon: '💪',
    points: 200,
    progress: 4,
    total: 7,
    category: 'health',
  },
  {
    id: '5',
    title: 'Social Butterfly',
    description: 'Connect with 50 friends',
    icon: '🦋',
    points: 250,
    progress: 23,
    total: 50,
    category: 'social',
  },
  {
    id: '6',
    title: 'Story Teller',
    description: 'Share 5 stories',
    icon: '📖',
    points: 100,
    progress: 2,
    total: 5,
    category: 'awareness',
  },
  {
    id: '7',
    title: 'Live Streamer',
    description: 'Host your first live session',
    icon: '🎥',
    points: 300,
    category: 'social',
  },
  {
    id: '8',
    title: 'TB Warrior',
    description: 'Complete all TB awareness modules',
    icon: '🛡️',
    points: 500,
    progress: 60,
    total: 100,
    category: 'awareness',
  },
  {
    id: '9',
    title: 'Supporter',
    description: 'Help 10 people with questions',
    icon: '🤝',
    points: 150,
    progress: 6,
    total: 10,
    category: 'community',
  },
  {
    id: '10',
    title: 'Streak Master',
    description: 'Maintain a 30-day streak',
    icon: '🔥',
    points: 400,
    progress: 12,
    total: 30,
    category: 'health',
  },
]

// ================================================================================
// ACHIEVEMENT CARD COMPONENT
// ================================================================================
interface AchievementCardProps {
  achievement: Achievement
}

const AchievementCard = React.memo(({ achievement }: AchievementCardProps) => {
  const isUnlocked = !!achievement.unlockedAt
  const progressPercent = achievement.progress && achievement.total
    ? Math.round((achievement.progress / achievement.total) * 100)
    : 0

  return (
    <GovCard className={`h-full ${!isUnlocked ? 'opacity-75' : ''}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0 ${
          isUnlocked
            ? 'bg-gradient-to-br from-emerald-400 to-teal-500'
            : 'bg-gradient-to-br from-gray-200 to-gray-300'
        }`}>
          {achievement.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 text-sm">{achievement.title}</h3>
            {isUnlocked && (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                Unlocked
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-2 line-clamp-2">{achievement.description}</p>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-medium ${isUnlocked ? 'text-emerald-600' : 'text-gray-400'}`}>
              +{achievement.points} pts
            </span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {!isUnlocked && achievement.progress !== undefined && achievement.total && (
        <div className="mt-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-500">Progress</span>
            <span className="text-gray-600 font-medium">{achievement.progress}/{achievement.total}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Unlocked Date */}
      {isUnlocked && achievement.unlockedAt && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">
            Unlocked: {new Date(achievement.unlockedAt).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })}
          </span>
        </div>
      )}
    </GovCard>
  )
})

AchievementCard.displayName = 'AchievementCard'

// ================================================================================
// STAT CARD COMPONENT
// ================================================================================
interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  gradient: string
}

const StatCard = React.memo(({ icon, label, value, gradient }: StatCardProps) => (
  <GovCard className="p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${gradient} flex items-center justify-center`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </GovCard>
))

StatCard.displayName = 'StatCard'

// ================================================================================
// FILTER BUTTON COMPONENT
// ================================================================================
interface FilterButtonProps {
  category: typeof CATEGORY_FILTERS[number]
  isActive: boolean
  onClick: () => void
}

const FilterButton = React.memo(({ category, isActive, onClick }: FilterButtonProps) => {
  const Icon = category.icon

  return (
    <GovButton
      variant={isActive ? 'primary' : 'secondary'}
      size="sm"
      onClick={onClick}
      className="flex items-center gap-1.5"
    >
      <Icon className="w-4 h-4" />
      {category.label}
    </GovButton>
  )
})

FilterButton.displayName = 'FilterButton'

// ================================================================================
// MAIN PAGE COMPONENT
// ================================================================================
const AchievementsPage = () => {
  const [filter, setFilter] = useState<string>('all')

  // Memoized stats
  const stats = useMemo(() => {
    const unlockedCount = MOCK_ACHIEVEMENTS.filter(a => a.unlockedAt).length
    const totalPoints = MOCK_ACHIEVEMENTS
      .filter(a => a.unlockedAt)
      .reduce((sum, a) => sum + a.points, 0)
    const completionPercent = Math.round((unlockedCount / MOCK_ACHIEVEMENTS.length) * 100)

    return { unlockedCount, totalPoints, completionPercent }
  }, [])

  // Memoized filtered achievements
  const filteredAchievements = useMemo(() => {
    return filter === 'all'
      ? MOCK_ACHIEVEMENTS
      : MOCK_ACHIEVEMENTS.filter(a => a.category === filter)
  }, [filter])

  // Filter handler
  const handleFilterChange = useCallback((categoryId: string) => {
    setFilter(categoryId)
  }, [])

  return (
    <GovLayout>
      <GovPageTitle
        title="Achievements"
        subtitle="Track your progress and earn rewards"
        icon={Trophy}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard
          icon={<Trophy className="w-5 h-5 text-white" />}
          label="Unlocked"
          value={`${stats.unlockedCount}/${MOCK_ACHIEVEMENTS.length}`}
          gradient="bg-gradient-to-br from-yellow-400 to-orange-500"
        />
        <StatCard
          icon={<Star className="w-5 h-5 text-white" />}
          label="Total Points"
          value={stats.totalPoints.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-blue-400 to-blue-600"
        />
        <StatCard
          icon={<Award className="w-5 h-5 text-white" />}
          label="Completion"
          value={`${stats.completionPercent}%`}
          gradient="bg-gradient-to-br from-purple-400 to-purple-600"
        />
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {CATEGORY_FILTERS.map((category) => (
          <FilterButton
            key={category.id}
            category={category}
            isActive={filter === category.id}
            onClick={() => handleFilterChange(category.id)}
          />
        ))}
      </div>

      {/* Achievements Grid */}
      {filteredAchievements.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAchievements.map((achievement) => (
            <AchievementCard key={achievement.id} achievement={achievement} />
          ))}
        </div>
      ) : (
        <GovEmptyState
          icon="🏆"
          title="No achievements found"
          description="Complete activities to unlock achievements"
        />
      )}
    </GovLayout>
  )
}

export default React.memo(AchievementsPage)